/**
 * EmailRecipientsChipField — Gmail-style recipient chips.
 *
 * Valid emails become chips on Enter / Tab / Space / comma / blur.
 * While typing a valid address, a suggestion dropdown appears (like Gmail).
 */

import React, {
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  ListItemAvatar,
  ListItemText,
  TextField,
  createFilterOptions,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useTranslation } from "react-i18next";
import {
  isValidEmail,
  parseEmailString,
  emailArrayToString,
  deduplicateEmails,
  extractEmailsFromText,
} from "../utils/email-recipient-chip-utils";
import {
  colorTokens,
  radiusTokens,
} from "@/widgets/frontdesk/design-system";

export interface EmailRecipientsChipFieldProps {
  /** Comma-separated email string (for backend compatibility) */
  emails: string;
  /** Callback when emails change */
  onChange: (emails: string) => void;
  /** Placeholder text when no recipients */
  placeholder?: string;
  /** Whether field is read-only */
  readOnly?: boolean;
  /** Auto-focus the input when mounted */
  autoFocus?: boolean;
}

const filter = createFilterOptions<string>();

export const EmailRecipientsChipField: React.FC<
  EmailRecipientsChipFieldProps
> = ({
  emails,
  onChange,
  placeholder = "Enter email addresses",
  readOnly = false,
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const isRTL = dir === "rtl";
  const [inputValue, setInputValue] = useState("");

  const selected = useMemo(
    () => deduplicateEmails(parseEmailString(emails)),
    [emails]
  );

  const commitEmails = useCallback(
    (next: string[]) => {
      onChange(emailArrayToString(deduplicateEmails(next)));
    },
    [onChange]
  );

  const tryCommitDraft = useCallback(
    (raw: string): boolean => {
      const trimmed = raw.trim();
      if (!trimmed) return false;

      const extracted = extractEmailsFromText(trimmed);
      const toAdd =
        extracted.length > 0
          ? extracted
          : isValidEmail(trimmed)
          ? [trimmed]
          : [];

      if (toAdd.length === 0) return false;
      commitEmails([...selected, ...toAdd]);
      setInputValue("");
      return true;
    },
    [commitEmails, selected]
  );

  // Suggestion when the draft looks like a complete valid email (Gmail)
  const options = useMemo(() => {
    const draft = inputValue.trim();
    if (!draft || !isValidEmail(draft)) return [];
    if (selected.some((e) => e.toLowerCase() === draft.toLowerCase())) {
      return [];
    }
    return [draft];
  }, [inputValue, selected]);

  return (
    <Autocomplete
      multiple
      freeSolo
      fullWidth
      size="small"
      disabled={readOnly}
      options={options}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const draft = params.inputValue.trim();
        if (
          draft &&
          isValidEmail(draft) &&
          !filtered.some((o) => o.toLowerCase() === draft.toLowerCase()) &&
          !selected.some((e) => e.toLowerCase() === draft.toLowerCase())
        ) {
          filtered.push(draft);
        }
        return filtered;
      }}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, value, reason) => {
        if (reason === "reset") return;
        // Space / comma / semicolon after a valid email → chip (Gmail)
        if (
          reason === "input" &&
          (value.endsWith(" ") ||
            value.endsWith(",") ||
            value.endsWith(";"))
        ) {
          const draft = value.slice(0, -1).trim();
          if (draft && tryCommitDraft(draft)) return;
        }
        setInputValue(value);
      }}
      onChange={(_, newValue) => {
        const emailsOnly = newValue
          .map((v) => (typeof v === "string" ? v.trim() : ""))
          .filter(Boolean);
        // Flatten pasted / name <email> forms
        const expanded: string[] = [];
        for (const item of emailsOnly) {
          const extracted = extractEmailsFromText(item);
          if (extracted.length > 0) {
            expanded.push(...extracted);
          } else {
            expanded.push(item);
          }
        }
        commitEmails(expanded);
        setInputValue("");
      }}
      onBlur={() => {
        if (inputValue.trim()) {
          tryCommitDraft(inputValue);
        }
      }}
      forcePopupIcon={false}
      clearOnBlur={false}
      selectOnFocus
      handleHomeEndKeys
      open={options.length > 0 && inputValue.trim().length > 0}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option}>
          <ListItemAvatar sx={{ minWidth: 40 }}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                bgcolor: colorTokens.primary[100],
                color: colorTokens.primary[700],
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 18 }} />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={option}
            secondary={option}
            primaryTypographyProps={{
              fontWeight: 600,
              fontSize: "0.875rem",
              color: colorTokens.neutral[900],
              sx: { direction: "ltr" },
            }}
            secondaryTypographyProps={{
              fontSize: "0.75rem",
              color: colorTokens.neutral[500],
              sx: { direction: "ltr" },
            }}
          />
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          const valid = isValidEmail(option);
          return (
            <Chip
              key={key}
              {...tagProps}
              size="small"
              label={option}
              sx={{
                height: 24,
                fontSize: "0.8125rem",
                fontWeight: 500,
                direction: "ltr",
                borderRadius: `${radiusTokens.md}px`,
                backgroundColor: valid
                  ? colorTokens.primary[50]
                  : colorTokens.error[50],
                color: valid
                  ? colorTokens.primary[700]
                  : colorTokens.error[700],
                border: `1px solid ${
                  valid ? colorTokens.primary[200] : colorTokens.error[300]
                }`,
                "& .MuiChip-label": { px: 1 },
                "& .MuiChip-deleteIcon": {
                  fontSize: 16,
                  color: valid
                    ? colorTokens.primary[600]
                    : colorTokens.error[600],
                  "&:hover": {
                    color: valid
                      ? colorTokens.primary[800]
                      : colorTokens.error[800],
                  },
                },
              }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={selected.length === 0 ? placeholder : ""}
          autoFocus={autoFocus}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
          }}
          inputProps={{
            ...params.inputProps,
            style: {
              ...((params.inputProps as { style?: React.CSSProperties })
                ?.style || {}),
              direction: "ltr",
              textAlign: isRTL ? "right" : "left",
            },
          }}
          sx={{
            "&.MuiFormControl-root.MuiFormControl-root.MuiFormControl-root": { minHeight: "0 !important" },
            "& .MuiInputBase-root": {
              flexWrap: "wrap",
              gap: 0.5,
              py: "1px",
              fontSize: "0.875rem",
            },
            "& .MuiAutocomplete-tag": { my: 0, mx: 0 },
            "& .MuiAutocomplete-input": {
              minWidth: 120,
              py: 0.25,
              px: 0,
            },
            // Outranks the modern theme's `.MuiInputBase-input { height: 40px; padding: 9px 12px }` (!important).
            "& .MuiInputBase-input.MuiInputBase-input": {
              height: "auto !important",
              minHeight: "0 !important",
              padding: "2px 0 !important",
              lineHeight: "20px !important",
            },
          }}
        />
      )}
      componentsProps={{
        paper: {
          sx: {
            mt: 0.5,
            borderRadius: `${radiusTokens.lg}px`,
            border: `1px solid ${colorTokens.neutral[200]}`,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            direction: "ltr",
          },
        },
      }}
      sx={{
        width: "100%",
        "&.MuiAutocomplete-root.MuiAutocomplete-root": { minHeight: "0 !important" },
        // MUI reserves the clear button's slot on the physical right; mirror it in RTL.
        ...(isRTL && {
          "&.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot": { paddingRight: 0, paddingLeft: "30px" },
          "& .MuiAutocomplete-endAdornment": { right: "auto", left: 0 },
        }),
        "& .MuiAutocomplete-inputRoot": {
          padding: 0,
          flexWrap: "wrap",
        },
      }}
    />
  );
};
